export const YOUTUBE_EMBED_FRAME = {
  width: 1697,
  height: 602,
};

const YOUTUBE_ID_PATTERN = /^[a-zA-Z0-9_-]{11}$/;

const INVALID_HOST_MESSAGE =
  "Use a YouTube watch, embed, Shorts, or youtu.be link — not a channel or playlist URL.";

const extractIframeSrc = (value = "") => {
  const match = String(value).match(/src=["']([^"']+)["']/i);
  return match ? match[1] : value;
};

const buildEmbedUrl = (videoId) =>
  `https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1&playsinline=1`;

const resolveVideoId = (parsedUrl) => {
  const host = parsedUrl.hostname.replace(/^www\./i, "").replace(/^m\./i, "");

  if (host === "youtu.be") {
    return parsedUrl.pathname.split("/").filter(Boolean)[0] || "";
  }

  if (host === "youtube.com" || host === "youtube-nocookie.com") {
    const segments = parsedUrl.pathname.split("/").filter(Boolean);

    if (segments[0] === "embed" && segments[1]) return segments[1];
    if (segments[0] === "shorts" && segments[1]) return segments[1];
    if (segments[0] === "live" && segments[1]) return segments[1];
    if (segments[0] === "watch") return parsedUrl.searchParams.get("v") || "";
  }

  return "";
};

export const parseYouTubeEmbedInput = (input = "") => {
  const raw = String(input || "").trim();
  if (!raw) {
    return { videoId: "", embedUrl: "", error: null };
  }

  const candidate = extractIframeSrc(raw);

  try {
    const parsedUrl = new URL(
      candidate.startsWith("http") ? candidate : `https://${candidate}`
    );
    const videoId = resolveVideoId(parsedUrl);

    if (!videoId || !YOUTUBE_ID_PATTERN.test(videoId)) {
      return {
        videoId: "",
        embedUrl: "",
        error: INVALID_HOST_MESSAGE,
      };
    }

    return {
      videoId,
      embedUrl: buildEmbedUrl(videoId),
      error: null,
    };
  } catch {
    return {
      videoId: "",
      embedUrl: "",
      error: "Enter a valid YouTube URL or iframe embed code.",
    };
  }
};

export const normalizeYouTubeEmbedInput = (input = "") => {
  const parsed = parseYouTubeEmbedInput(input);
  return parsed.error ? "" : parsed.embedUrl;
};

export const getYouTubeEmbedValidationMessage = (input = "") => {
  const raw = String(input || "").trim();
  if (!raw) return null;
  return parseYouTubeEmbedInput(raw).error;
};

export const isYouTubeHighlightVisible = (highlightVideo = {}) =>
  Boolean(String(highlightVideo?.embedUrl || "").trim());

export const getYouTubeVideoId = (input = "") =>
  parseYouTubeEmbedInput(input).videoId || "";

export const normalizeYouTubeEmbedUrl = (input = "") => {
  const parsed = parseYouTubeEmbedInput(input);
  return parsed.error ? String(input || "").trim() : parsed.embedUrl;
};

/** @deprecated prefer getYouTubeVideoId */
export const parseYouTubeEmbedUrl = (embedUrl = "") => getYouTubeVideoId(embedUrl);

export const getYouTubeThumbnailUrl = (videoId = "", quality = "hqdefault") =>
  videoId ? `https://i.ytimg.com/vi/${videoId}/${quality}.jpg` : "";

export const appendYouTubeAutoplay = (embedUrl = "") => {
  const url = String(embedUrl || "").trim();
  if (!url) return "";
  return url.includes("?") ? `${url}&autoplay=1` : `${url}?autoplay=1`;
};
